import React, { useState } from "react";
import {
  faCheck as _faCheck,
  faCog as _faCog,
  faInfo,
  faSearch,
  faUser,
  faWarning,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  // Atoms
  Button,
  type ButtonSize as _ButtonSize,
  // Types
  type ButtonVariant,
  Checkbox,
  type CheckboxSize as _CheckboxSize,
  LinkButton,
  Loading,
  LoadingBar,
  Modal,
  // Molecules
  MultiSelect,
  type MultiSelectOption,
  RadioButton,
  SearchInput,
  Segmented,
  type SegmentOption,
  Select,
  type SortableColumn,
  SortableDataTable,
  Textarea,
  TextInput,
  Tooltip,
} from "@perfectserve/library";

interface LibraryDemoProps {
  isOpen: boolean;
  onClose: () => void;
}

const LibraryDemo = ({ isOpen, onClose }: LibraryDemoProps) => {
  // State for interactive examples
  const [checkboxStates, setCheckboxStates] = useState({
    default: false,
    checked: true,
    indeterminate: false,
  });
  const [radioValue, setRadioValue] = useState("option1");
  const [segmentedValue, setSegmentedValue] = useState("tab1");
  const [selectValue, setSelectValue] = useState("");
  const [textInputValue, setTextInputValue] = useState("");
  const [textareaValue, setTextareaValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [multiSelectValues, setMultiSelectValues] = useState<string[]>([]);

  // Sample data for components
  const segmentedOptions: SegmentOption[] = [
    { value: "tab1", label: "Tab 1" },
    { value: "tab2", label: "Tab 2" },
    { value: "tab3", label: "Tab 3" },
  ];

  const selectOptions = [
    { value: "option1", label: "Option 1" },
    { value: "option2", label: "Option 2" },
    { value: "option3", label: "Option 3" },
  ];

  const multiSelectOptions: MultiSelectOption[] = [
    { value: "apple", label: "Apple" },
    { value: "banana", label: "Banana" },
    { value: "cherry", label: "Cherry" },
    { value: "date", label: "Date" },
  ];

  const tableColumns: SortableColumn[] = [
    { key: "name", label: "Name", sortable: true },
    { key: "age", label: "Age", sortable: true },
    { key: "city", label: "City", sortable: true },
  ];

  const tableData = [
    { name: "John Doe", age: 30, city: "New York" },
    { name: "Jane Smith", age: 25, city: "Los Angeles" },
    { name: "Bob Johnson", age: 35, city: "Chicago" },
  ];

  const CodeBlock = ({ children }: { children: string }) => (
    <pre
      style={{
        backgroundColor: "#f8f9fa",
        border: "1px solid #e9ecef",
        borderRadius: "4px",
        padding: "12px",
        fontSize: "12px",
        overflow: "auto",
        margin: "8px 0",
      }}
    >
      <code>{children}</code>
    </pre>
  );

  const ComponentSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div style={{ marginBottom: "40px" }}>
      <h3 style={{ color: "#374B94", marginBottom: "20px", fontSize: "18px", fontWeight: "600" }}>{title}</h3>
      {children}
    </div>
  );

  const VariantGrid = ({ children }: { children: React.ReactNode }) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "16px",
        marginBottom: "16px",
      }}
    >
      {children}
    </div>
  );

  const VariantItem = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div
      style={{
        padding: "16px",
        border: "1px solid #e9ecef",
        borderRadius: "6px",
        backgroundColor: "#fff",
      }}
    >
      <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px", fontWeight: "500" }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "40px" }}>
        {children}
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Library Component Demonstration">
      <div style={{ height: "80vh", overflow: "auto", padding: "20px" }}>
        {/* ATOMS SECTION */}
        <div style={{ marginBottom: "50px" }}>
          <h2 style={{ color: "#374B94", marginBottom: "30px", fontSize: "24px", fontWeight: "700" }}>
            🔬 Atoms - Basic Building Blocks
          </h2>

          {/* Button Component */}
          <ComponentSection title="Button">
            <h4 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "600" }}>Variants</h4>
            <VariantGrid>
              {(["primary", "secondary", "link", "danger", "success", "warning", "info"] as ButtonVariant[]).map(
                (variant) => (
                  <VariantItem key={variant} label={variant}>
                    <Button variant={variant} onClick={() => alert(`${variant} clicked!`)}>
                      {variant}
                    </Button>
                  </VariantItem>
                )
              )}
            </VariantGrid>

            <h4 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "600" }}>States & Icons</h4>
            <VariantGrid>
              <VariantItem label="With Icon">
                <Button icon={faUser} onClick={() => alert("Icon button clicked!")}>
                  User
                </Button>
              </VariantItem>
              <VariantItem label="Loading">
                <Button loading>Loading...</Button>
              </VariantItem>
              <VariantItem label="Disabled">
                <Button disabled>Disabled</Button>
              </VariantItem>
              <VariantItem label="Full Width">
                <Button fullWidth>Full Width</Button>
              </VariantItem>
            </VariantGrid>

            <CodeBlock>
              {`import { Button } from "@perfectserve/library";
import { faUser } from "@fortawesome/pro-solid-svg-icons";

<Button variant="primary" size="medium" onClick={handleClick}>
  Primary Button
</Button>

<Button variant="secondary" icon={faUser} iconPosition="left">
  With Icon
</Button>

<Button loading>Loading...</Button>`}
            </CodeBlock>
          </ComponentSection>

          {/* Checkbox Component */}
          <ComponentSection title="Checkbox">
            <h4 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "600" }}>States & Sizes</h4>
            <VariantGrid>
              <VariantItem label="Unchecked">
                <Checkbox
                  checked={checkboxStates.default}
                  onChange={(checked) => setCheckboxStates({ ...checkboxStates, default: checked })}
                  label="Unchecked"
                />
              </VariantItem>
              <VariantItem label="Checked">
                <Checkbox
                  checked={checkboxStates.checked}
                  onChange={(checked) => setCheckboxStates({ ...checkboxStates, checked: checked })}
                  label="Checked"
                />
              </VariantItem>
              <VariantItem label="Small Size">
                <Checkbox size="small" checked={true} onChange={() => {}} label="Small" />
              </VariantItem>
              <VariantItem label="Large Size">
                <Checkbox size="large" checked={true} onChange={() => {}} label="Large" />
              </VariantItem>
            </VariantGrid>

            <CodeBlock>
              {`import { Checkbox } from "@perfectserve/library";

<Checkbox
  checked={isChecked}
  onChange={setIsChecked}
  label="Accept terms"
  size="medium"
/>`}
            </CodeBlock>
          </ComponentSection>

          {/* Loading Components */}
          <ComponentSection title="Loading">
            <h4 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "600" }}>Loading Spinner</h4>
            <VariantGrid>
              <VariantItem label="Default">
                <Loading />
              </VariantItem>
            </VariantGrid>

            <h4 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "600" }}>Loading Bar</h4>
            <div style={{ marginBottom: "16px" }}>
              <LoadingBar width="100%" height="8px" duration={2} />
            </div>

            <CodeBlock>
              {`import { Loading, LoadingBar } from "@perfectserve/library";

<Loading />
<LoadingBar width="100%" height="8px" duration={2} />`}
            </CodeBlock>
          </ComponentSection>

          {/* Form Controls */}
          <ComponentSection title="Form Controls">
            <h4 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "600" }}>Radio Buttons</h4>
            <div style={{ marginBottom: "16px" }}>
              <RadioButton
                name="demo-radio"
                value="option1"
                checked={radioValue === "option1"}
                onChange={setRadioValue}
                label="Option 1"
              />
              <RadioButton
                name="demo-radio"
                value="option2"
                checked={radioValue === "option2"}
                onChange={setRadioValue}
                label="Option 2"
              />
            </div>

            <h4 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "600" }}>Segmented Control</h4>
            <div style={{ marginBottom: "16px" }}>
              <Segmented
                name="demo-segmented"
                value={segmentedValue}
                onChange={setSegmentedValue}
                options={segmentedOptions}
              />
            </div>

            <h4 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "600" }}>Select</h4>
            <div style={{ marginBottom: "16px", maxWidth: "300px" }}>
              <Select
                value={selectValue}
                onChange={(value) => setSelectValue(value as string)}
                options={selectOptions}
                placeholder="Choose an option..."
              />
            </div>

            <h4 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "600" }}>Text Input</h4>
            <div style={{ marginBottom: "16px", maxWidth: "300px" }}>
              <TextInput
                value={textInputValue}
                onChange={setTextInputValue}
                placeholder="Enter text..."
                label="Text Input"
              />
            </div>

            <h4 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "600" }}>Textarea</h4>
            <div style={{ marginBottom: "16px", maxWidth: "300px" }}>
              <Textarea
                value={textareaValue}
                onChange={setTextareaValue}
                placeholder="Enter multiple lines..."
                label="Textarea"
                rows={3}
              />
            </div>

            <CodeBlock>
              {`import { RadioButton, Segmented, Select, TextInput, Textarea } from "@perfectserve/library";

<RadioButton
  name="group"
  value="option1"
  checked={value === "option1"}
  onChange={setValue}
  label="Option 1"
/>

<Segmented
  name="tabs"
  value={activeTab}
  onChange={setActiveTab}
  options={[
    { value: "tab1", label: "Tab 1" },
    { value: "tab2", label: "Tab 2" }
  ]}
/>

<TextInput
  value={text}
  onChange={setText}
  placeholder="Enter text..."
  label="Input Label"
/>`}
            </CodeBlock>
          </ComponentSection>

          {/* LinkButton */}
          <ComponentSection title="LinkButton">
            <VariantGrid>
              <VariantItem label="Default">
                <LinkButton onClick={() => alert("Link clicked!")}>Link Button</LinkButton>
              </VariantItem>
              <VariantItem label="With Icon">
                <LinkButton
                  onClick={() => alert("Link clicked!")}
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <FontAwesomeIcon icon={faSearch} />
                  Search
                </LinkButton>
              </VariantItem>
            </VariantGrid>

            <CodeBlock>
              {`import { LinkButton } from "@perfectserve/library";

<LinkButton onClick={handleClick}>
  Link Button
</LinkButton>`}
            </CodeBlock>
          </ComponentSection>
        </div>

        {/* MOLECULES SECTION */}
        <div>
          <h2 style={{ color: "#374B94", marginBottom: "30px", fontSize: "24px", fontWeight: "700" }}>
            🧬 Molecules - Compound Components
          </h2>

          {/* SearchInput */}
          <ComponentSection title="SearchInput">
            <div style={{ marginBottom: "16px", maxWidth: "300px" }}>
              <SearchInput value={searchValue} onChange={setSearchValue} placeholder="Search..." size="medium" />
            </div>

            <CodeBlock>
              {`import { SearchInput } from "@perfectserve/library";

<SearchInput
  value={searchValue}
  onChange={setSearchValue}
  placeholder="Search..."
  size="medium"
/>`}
            </CodeBlock>
          </ComponentSection>

          {/* MultiSelect */}
          <ComponentSection title="MultiSelect">
            <div style={{ marginBottom: "16px", maxWidth: "300px" }}>
              <MultiSelect
                options={multiSelectOptions}
                selectedValues={multiSelectValues}
                onChange={setMultiSelectValues}
                placeholder="Select fruits..."
                searchPlaceholder="Search fruits..."
              />
            </div>

            <CodeBlock>
              {`import { MultiSelect } from "@perfectserve/library";

const options = [
  { value: "apple", label: "Apple" },
  { value: "banana", label: "Banana" }
];

<MultiSelect
  options={options}
  selectedValues={selectedValues}
  onChange={setSelectedValues}
  placeholder="Select items..."
/>`}
            </CodeBlock>
          </ComponentSection>

          {/* SortableDataTable */}
          <ComponentSection title="SortableDataTable">
            <div style={{ marginBottom: "16px" }}>
              <SortableDataTable
                data={tableData}
                columns={tableColumns}
                defaultSortKey="name"
                defaultSortDirection="asc"
              />
            </div>

            <CodeBlock>
              {`import { SortableDataTable } from "@perfectserve/library";

const columns = [
  { key: "name", label: "Name", sortable: true },
  { key: "age", label: "Age", sortable: true },
  { key: "city", label: "City", sortable: false }
];

<SortableDataTable
  data={data}
  columns={columns}
  defaultSortKey="name"
  defaultSortDirection="asc"
/>`}
            </CodeBlock>
          </ComponentSection>

          {/* Tooltip */}
          <ComponentSection title="Tooltip">
            <VariantGrid>
              <VariantItem label="Top Position">
                <Tooltip content="This is a helpful tooltip!" position="top">
                  <Button variant="secondary">
                    <FontAwesomeIcon icon={faInfo} />
                    Hover me (Top)
                  </Button>
                </Tooltip>
              </VariantItem>
              <VariantItem label="Bottom Position">
                <Tooltip content="Tooltip appears below the button" position="bottom">
                  <Button variant="info">
                    <FontAwesomeIcon icon={faWarning} />
                    Hover me (Bottom)
                  </Button>
                </Tooltip>
              </VariantItem>
            </VariantGrid>

            <CodeBlock>
              {`import { Tooltip } from "@perfectserve/library";

<Tooltip content="Helpful information" position="top">
  <Button>Hover for tooltip</Button>
</Tooltip>

<Tooltip content="Bottom tooltip" position="bottom">
  <Button>Bottom tooltip</Button>
</Tooltip>`}
            </CodeBlock>
          </ComponentSection>
        </div>
      </div>
    </Modal>
  );
};

export default LibraryDemo;
